import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, PencilSimple, Trash, House } from "@phosphor-icons/react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { ownerNav } from "@/config/nav";
import api, { fileUrl, formatINR } from "@/lib/api";
import { SkeletonRow } from "@/components/Loaders";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  inactive: "bg-stone-200 text-stone-600",
};

export default function OwnerProperties() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  const load = () => api.get("/owner/properties").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const toggleActive = async (p) => {
    if (p.status === "pending" || p.status === "rejected") {
      return toast.error("Property must be approved by admin first.");
    }
    const next = p.status === "approved" ? "inactive" : "approved";
    try {
      await api.patch(`/properties/${p.id}/status`, { status: next });
      toast.success(next === "approved" ? "Property activated" : "Property deactivated");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    await api.delete(`/properties/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <DashboardLayout nav={ownerNav} title="Owner">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">My Properties</h1>
        <Button data-testid="add-property-btn" onClick={() => navigate("/owner/properties/new")} className="gap-2 rounded-full bg-primary hover:bg-green-900">
          <Plus size={18} /> Add Property
        </Button>
      </div>

      <div className="mt-6 space-y-3">
        {items === null ? (
          [1, 2, 3].map((i) => <SkeletonRow key={i} />)
        ) : items.length === 0 ? (
          <EmptyState icon={House} title="No properties yet" message="Add your first property to start receiving bookings." action={<Button className="rounded-full bg-primary hover:bg-green-900" onClick={() => navigate("/owner/properties/new")}>Add Property</Button>} />
        ) : (
          items.map((p) => (
            <div key={p.id} data-testid={`owner-property-${p.id}`} className="flex flex-col gap-3 rounded-2xl border border-stone-200 bg-white p-4 sm:flex-row sm:items-center">
              <img src={fileUrl(p.images?.[0])} alt={p.name} className="h-20 w-full rounded-xl object-cover sm:w-28" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-heading font-semibold text-stone-900">{p.name}</p>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLE[p.status]}`}>{p.status === "approved" ? "Active" : p.status}</span>
                </div>
                <p className="text-sm text-stone-500">{p.category} · {p.location}</p>
                <p className="mt-1 font-heading font-bold text-stone-900">{formatINR(p.prices?.starting)}</p>
              </div>
              <div className="flex items-center gap-3">
                {(p.status === "approved" || p.status === "inactive") && (
                  <div className="flex items-center gap-2">
                    <Switch data-testid={`toggle-${p.id}`} checked={p.status === "approved"} onCheckedChange={() => toggleActive(p)} />
                    <span className="text-xs text-stone-500">Active</span>
                  </div>
                )}
                <Button data-testid={`edit-${p.id}`} variant="outline" size="icon" className="rounded-xl" onClick={() => navigate(`/owner/properties/${p.id}/edit`)}><PencilSimple size={16} /></Button>
                <Button data-testid={`delete-${p.id}`} variant="outline" size="icon" className="rounded-xl text-destructive" onClick={() => del(p.id)}><Trash size={16} /></Button>
              </div>
            </div>
          ))
        )}
      </div>
    </DashboardLayout>
  );
}
